import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type TaskDefinition, TaskDefinitionForm } from './TaskDefinitionForm';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks/add',
)({
    staticData: {
        metadata: {
            title: 'Create new task',
        },
        ui: {
            title: 'Create new task',
        },
        presentation: 'overlay',
        modules: ['claim'],
    },
    component: AddTaskDefinition,
});

function AddTaskDefinition() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { taskListId } = Route.useParams();
    const { mutate: createTaskDefinition, isPending: isCreating } =
        trpc.taskDefinition.createTaskDefinition.useMutation({
            onError(error) {
                errorMessage.show(error);
            },
        });

    const handleSubmit = (values: TaskDefinition) => {
        message.loading('Creating task...');
        createTaskDefinition(
            {
                name: values.name ?? '',
                description: values.description ?? '',
                dueDateOffset: values.dueDateOffset ?? 1,
                reminderOffsets: values.reminderOffsets,
                taskListId,
            },
            {
                onSuccess: () => {
                    message.success('Task created');
                    trpcUtils.taskDefinition.listTaskDefinitions.invalidate();
                    setOpen(false);
                },
            },
        );
    };
    return (
        <PageController route={Route}>
            <TaskDefinitionForm
                open={open}
                mode="add"
                data={{ name: '' }}
                state={match({ isCreating })
                    .with({ isCreating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/claims-products/claims/task-lists/search/$taskListId/tasks',
                        params: { orgId, taskListId },
                    });
                }}
            />
        </PageController>
    );
}
